import checkPermission from '../../utils/checkPermission';
import {
  HelpCenterContentDefinition,
  HelpCenterDisplayMode,
} from './types';

type HelpCenterModule = {
  default: HelpCenterContentDefinition;
};

const context: WebpackRequireContext =
  require.context('./content', true, /(?<!_TEMPLATE)\/index\.tsx$/);

const contentDefinitions: HelpCenterContentDefinition[] = context
  .keys()
  .map((key: string) => context<HelpCenterModule>(key).default)
  .sort((left: HelpCenterContentDefinition, right: HelpCenterContentDefinition) => {
    const leftOrder = left.order ?? 9999;
    const rightOrder = right.order ?? 9999;

    if (leftOrder !== rightOrder) return leftOrder - rightOrder;

    return left.slug.localeCompare(right.slug);
  });

export const canAccessHelpCenterContent = (definition: HelpCenterContentDefinition): boolean => {
  const visibility = definition.visibility ?? { type: 'public' as const };

  if (visibility.type === 'public') return true;

  return checkPermission(visibility.permission);
};

export const supportsHelpCenterDisplayMode = (
  definition: HelpCenterContentDefinition,
  mode: HelpCenterDisplayMode,
): boolean => {
  const allowedDisplayModes = definition.allowedDisplayModes ?? ['page', 'modal'];

  return allowedDisplayModes.includes(mode);
};

export const getAccessibleHelpCenterContent = (
  mode?: HelpCenterDisplayMode,
): HelpCenterContentDefinition[] => contentDefinitions.filter(
  (definition: HelpCenterContentDefinition) => {
    if (!canAccessHelpCenterContent(definition)) return false;
    if (mode && !supportsHelpCenterDisplayMode(definition, mode)) return false;

    return true;
  },
);

export const getHelpCenterContentBySlug = (
  slug: string,
  mode?: HelpCenterDisplayMode,
): HelpCenterContentDefinition | null => {
  const definition = contentDefinitions.find(
    (item: HelpCenterContentDefinition) => item.slug === slug,
  ) ?? null;

  if (!definition) return null;
  if (!canAccessHelpCenterContent(definition)) return null;
  if (mode && !supportsHelpCenterDisplayMode(definition, mode)) return null;

  return definition;
};
